import { useState } from 'react';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { localizeCategory, useCategories } from './catalogCategories';
import { modalBackdropCls, modalCardCls, overlayCls } from './overlay';

// What a catalog item is, as far as anything that edits one is concerned: a category
// and a name in each language.
//
// The drawing is not here. It is not edited — it is cropped out of the manual by
// whoever is saving the part, or it is already on the row — so it is shown (below) and
// sent by the caller, and there is nothing for this form to collect.
export type CatalogItemFields = {
  kind: 'part' | 'tool';
  nameEn: string;
  nameFr: string;
  category: string;
};

// The delete control inside a modal: a quiet button that asks once.
//
// Two steps, in place, because the alternative is a second dialog on top of the first
// asking about the first. `confirm` says what will be lost — a category takes its items
// with it — and nothing here decides anything: the caller deletes.
export function ModalDelete({
  label,
  confirm,
  onDelete,
  t,
}: {
  label: string;
  confirm: string;
  onDelete: () => Promise<void>;
  t: (k: string) => string;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:border-rose-400 hover:text-rose-600"
      >
        <Trash2 className="h-4 w-4" />
        {label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-xs text-slate-500">{confirm}</span>
      <button
        type="button"
        onClick={async () => {
          setBusy(true);
          try {
            await onDelete();
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        aria-label={label}
        className="rounded p-1.5 text-rose-500 transition-colors hover:bg-rose-500/10 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={() => setAsking(false)}
        aria-label={t('notice.catalog.cancel')}
        className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-300/40"
      >
        <X className="h-4 w-4" />
      </button>
    </span>
  );
}

// The one dialog for a catalog item, whether it exists yet or not: the parts table
// opens it to save a part, the Parts DB opens it to edit or delete one.
//
// One form for both, because they are one form — the fields a catalog item HAS. Two
// would drift the day one of them learns about a field the other does not.
//
// The caller owns the writing: this collects the fields and calls back. It is the only
// arrangement in which "create" and "edit" can share a dialog without it knowing which
// it is doing.
export function CatalogItemModal({
  title,
  initial,
  newSchematic,
  onSave,
  onDelete,
  deleteLabel,
  deleteConfirm,
  onClose,
}: {
  title: string;
  initial: CatalogItemFields;
  // The crop being saved right now — a data URL from the manual, shown so you see the
  // part you are naming. Only when creating; there is no new drawing when editing.
  newSchematic?: string | null;
  onSave: (fields: CatalogItemFields) => Promise<void>;
  // Absent when there is nothing to delete yet — the parts table's "add" has no item.
  onDelete?: () => Promise<void>;
  deleteLabel?: string;
  deleteConfirm?: string;
  onClose: () => void;
}) {
  const { t, language } = useLanguage();
  const categories = useCategories();
  const [nameEn, setNameEn] = useState(initial.nameEn);
  const [nameFr, setNameFr] = useState(initial.nameFr);
  const [category, setCategory] = useState(initial.category);
  // True while typing a brand-new category (the "+ New category" dropdown choice).
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enField = { label: t('notice.catalog.nameEn'), value: nameEn, set: setNameEn };
  const frField = { label: t('notice.catalog.nameFr'), value: nameFr, set: setNameFr };
  const nameFields = language === 'fr' ? [frField, enField] : [enField, frField];

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        kind: initial.kind,
        nameEn: nameEn.trim(),
        nameFr: nameFr.trim(),
        category: category.trim(),
      });
    } catch {
      setError(t('notice.catalog.saveError'));
      setSaving(false);
    }
  };

  const fieldCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40';
  const labelCls =
    'mb-1 block text-center text-xs font-semibold uppercase tracking-wide text-slate-500';

  return (
    <div className={`${overlayCls} ${modalBackdropCls}`} onClick={onClose}>
      <div
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto p-5 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mb-4">
          <h2 className="text-center text-lg font-bold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('notice.catalog.cancel')}
            className="absolute right-0 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Only when ADDING: the crop you are about to save, because it IS the part
              and the names/category are what you are being asked about it. When editing
              an existing part there is no picture here — its drawings and photos already
              live in the table, clickable and removable there. */}
          {newSchematic && (
            <div className="flex justify-center">
              <img
                src={newSchematic}
                alt={t('notice.catalog.schematic')}
                className="max-h-32 w-auto rounded-lg border border-slate-200 bg-white"
              />
            </div>
          )}

          <div>
            <label className={labelCls}>{t('notice.catalog.category')}</label>
            <select
              value={creatingCategory ? '__new__' : category}
              onChange={(e) => {
                if (e.target.value === '__new__') {
                  setCreatingCategory(true);
                  setCategory('');
                } else {
                  setCreatingCategory(false);
                  setCategory(e.target.value);
                }
              }}
              className={fieldCls}
            >
              <option value="">{t('notice.catalog.uncategorized')}</option>
              {/* The value stays the English identifier — it is what the catalog
                  stores and what every classifier matches against — and the label is
                  the language being read. A dropdown is one place the English name has
                  to survive a missing translation: an empty option cannot be picked. */}
              {categories.map((c) => (
                <option key={c.name} value={c.name}>
                  {localizeCategory(categories, c.name, language)}
                </option>
              ))}
              <option value="__new__">{t('notice.catalog.newCategory')}</option>
            </select>
            {creatingCategory && (
              <input
                autoFocus
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={t('notice.catalog.categoryPlaceholder')}
                className={`${fieldCls} mt-2`}
              />
            )}
          </div>

          {/* Both names, because the catalog stores both and this is where they are
              written. The one you are reading in leads: it is the one you can judge,
              and the other is the translation of it. */}
          {nameFields.map((f) => (
            <div key={f.label}>
              <label className={labelCls}>{f.label}</label>
              <input value={f.value} onChange={(e) => f.set(e.target.value)} className={fieldCls} />
            </div>
          ))}

          {error && <p className="text-center text-sm text-rose-600">{error}</p>}

          {/* The pair you came for, centred under the fields. Delete is below them
              rather than beside them: the two you might press by mistake are the two
              you can undo, and a row of three centres on the wrong one. */}
          <div className="mt-1 flex justify-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              {t('notice.catalog.cancel')}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? t('notice.catalog.saving') : t('notice.catalog.save')}
            </button>
          </div>
          {onDelete && (
            <div className="flex justify-center">
              <ModalDelete
                label={deleteLabel || t('notice.catalog.deleteItem')}
                confirm={deleteConfirm || t('notice.catalog.deleteItemConfirm')}
                onDelete={onDelete}
                t={t}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
